import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { PageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Clock, 
  MapPin, 
  Star, 
  Phone,
  Plus,
  Minus,
  ShoppingBag,
  ChevronRight,
  ChevronLeft,
  AlertTriangle,
  Leaf,
  Check,
  CalendarDays,
  ChevronDown,
  Camera,
  X,
  Loader2,
  Heart,
  Lock,
  Award,
  Gift,
  Stamp,
  CreditCard
} from 'lucide-react';
import { getFullImageUrl, RESTAURANT_PLACEHOLDER } from '../utils/images';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { checkIsOpen, getFormattedSchedule } from '../utils/openingHours';
import { toast } from 'sonner';
import axios from 'axios';
import { VerifyPinModal } from '../components/PinPad';
import { ShareButtons } from '../components/ShareButtons';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { getApiErrorMessage } from '../utils/errorHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RestaurantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart, clearAndAddToCart, itemCount, cart, isPreorderCart } = useCart();
  
  // Cart type mismatch confirmation
  const [cartMismatchDialog, setCartMismatchDialog] = useState({ open: false, pendingDish: null });
  
  const [restaurant, setRestaurant] = useState(null);
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDish, setSelectedDish] = useState(null);
  const [dishQuantity, setDishQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedIngredients, setSelectedIngredients] = useState({ base: [], extras: [] });
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  const [reviewsDistribution, setReviewsDistribution] = useState({});
  const [reviewsPhotosCount, setReviewsPhotosCount] = useState(0);
  const [showReviews, setShowReviews] = useState(false);
  const [showAllHours, setShowAllHours] = useState(false);
  const [reviewSortBy, setReviewSortBy] = useState('recent');
  const [reviewRatingFilter, setReviewRatingFilter] = useState(null);
  const [canReview, setCanReview] = useState({ can_review: false, reason: null });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '', photos: [] });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [favoriteDishes, setFavoriteDishes] = useState([]);
  const [favoriteRestaurant, setFavoriteRestaurant] = useState(false);
  
  // PIN protected categories
  const [unlockedCategories, setUnlockedCategories] = useState(new Set());
  const [pinModal, setPinModal] = useState({ open: false, category: null });
  
  // Gallery modal
  const [galleryModal, setGalleryModal] = useState({ open: false, index: 0 });
  
  // Loyalty program state
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [loyaltyProgram, setLoyaltyProgram] = useState(null);
  const [showLoyaltyModal, setShowLoyaltyModal] = useState(false);
  const [redeemingReward, setRedeemingReward] = useState(false);
  
  // Stamp card state
  const [stampCardData, setStampCardData] = useState(null);
  const [stampCardInfo, setStampCardInfo] = useState(null);
  const [showStampCardModal, setShowStampCardModal] = useState(false);

  // Calculate open status - prioritize API status over local calculation
  const hoursStatus = restaurant ? checkIsOpen(restaurant.opening_hours) : { isOpen: true, message: '' };
  
  // Use is_currently_open from API if available (includes manual close + hours check)
  const openStatus = {
    isOpen: restaurant?.is_currently_open !== undefined 
      ? restaurant.is_currently_open 
      : hoursStatus.isOpen,
    message: restaurant?.is_open === false 
      ? 'Temporairement fermé par le restaurant' 
      : hoursStatus.message
  };

  // Check if orders are accepted (separate from open/closed)
  const acceptingOrders = restaurant?.accepting_orders !== false;
  const orderBlockMessage = restaurant?.orders_paused_message || null;
  
  // Can place order = accepting_orders is true (regardless of physical open/close)
  const canOrder = acceptingOrders;
  
  const schedule = restaurant ? getFormattedSchedule(restaurant.opening_hours) : [];

  // Verify PIN for a locked category
  const handleVerifyPin = async (pin) => {
    if (!pinModal.category) return { valid: false };
    
    try {
      const response = await axios.post(
        `${API}/restaurants/${id}/categories/${pinModal.category.id}/verify-pin`,
        null,
        { params: { pin } }
      );
      
      if (response.data.valid) {
        // Add to unlocked set
        setUnlockedCategories(prev => new Set([...prev, pinModal.category.id]));
        
        // Update menu with the dishes
        setMenu(prevMenu => prevMenu.map(section => {
          if (section.category.id === pinModal.category.id) {
            return { ...section, dishes: response.data.dishes, locked: false };
          }
          return section;
        }));
        
        toast.success('Catégorie déverrouillée');
      }
      
      return response.data;
    } catch {
      return { valid: false, message: 'Erreur de vérification' };
    }
  };

  // Check if a category is accessible
  const isCategoryAccessible = (section) => {
    if (!section.locked) return true;
    return unlockedCategories.has(section.category.id);
  };

  // Handle click on a locked category
  const handleLockedCategoryClick = (section) => {
    if (isCategoryAccessible(section)) {
      setActiveCategory(section.category.id);
      // Scroll to the category section
      const el = document.getElementById(`category-${section.category.id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      setPinModal({ open: true, category: section.category });
    }
  };

  const fetchRestaurantData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/restaurants/${id}/menu`);
      setRestaurant(response.data.restaurant);
      setMenu(response.data.menu);
      if (response.data.menu.length > 0) {
        setActiveCategory(response.data.menu[0].category.id);
      }
    } catch {
      toast.error('Restaurant non trouvé');
      navigate('/restaurants');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchReviews = useCallback(async (sortBy = 'recent', ratingFilter = null) => {
    try {
      let url = `${API}/restaurants/${id}/reviews?sort_by=${sortBy}`;
      if (ratingFilter) {
        url += `&rating_filter=${ratingFilter}`;
      }
      const response = await axios.get(url);
      setReviews(response.data.reviews);
      setReviewsTotal(response.data.total);
      setReviewsDistribution(response.data.distribution || {});
      setReviewsPhotosCount(response.data.photos_count || 0);
    } catch {
      console.error('Error fetching reviews');
    }
  }, [id]);

  const checkCanReview = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await axios.get(`${API}/reviews/can-review/${id}`);
      setCanReview(response.data);
    } catch {
      console.error('Error checking review eligibility');
    }
  }, [id, isAuthenticated]);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (newReview.photos.length + files.length > 3) {
      toast.error('Maximum 3 photos par avis');
      return;
    }
    
    setUploadingPhotos(true);
    try {
      const uploadedPhotos = [];
      
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} est trop volumineux (max 5MB)`);
          continue;
        }
        
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        uploadedPhotos.push(response.data.url);
      }
      
      setNewReview(prev => ({
        ...prev,
        photos: [...prev.photos, ...uploadedPhotos]
      }));
      
      if (uploadedPhotos.length > 0) {
        toast.success(`${uploadedPhotos.length} photo(s) ajoutée(s)`);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'upload des photos');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const removePhoto = (index) => {
    setNewReview(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmitReview = async () => {
    if (!newReview.rating) {
      toast.error('Veuillez sélectionner une note');
      return;
    }
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, {
        restaurant_id: id,
        rating: newReview.rating,
        comment: newReview.comment || null,
        photos: newReview.photos.length > 0 ? newReview.photos : null
      });
      toast.success('Merci pour votre avis !');
      setShowReviewForm(false);
      setNewReview({ rating: 5, comment: '', photos: [] });
      fetchReviews(reviewSortBy, reviewRatingFilter);
      checkCanReview();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erreur lors de l'envoi de l'avis"));
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleReviewFilterChange = (sortBy, ratingFilter) => {
    setReviewSortBy(sortBy);
    setReviewRatingFilter(ratingFilter);
    fetchReviews(sortBy, ratingFilter);
  };

  // Fetch favorite status
  const fetchFavoriteStatus = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      
      const [dishesRes, restaurantRes] = await Promise.all([
        axios.get(`${API}/favorites/dishes`, {}),
        axios.get(`${API}/favorites/${id}/check`, {})
      ]);
      
      setFavoriteDishes(dishesRes.data.map(d => d.id));
      setFavoriteRestaurant(restaurantRes.data.is_favorite);
    } catch (error) {
      console.error('Error fetching favorite status:', error);
    }
  }, [id, isAuthenticated]);

  // Fetch loyalty data
  const fetchLoyaltyData = useCallback(async () => {
    try {
      // Get public program info
      const programRes = await axios.get(`${API}/loyalty/restaurant/${id}/program`);
      setLoyaltyProgram(programRes.data);
      
      // If authenticated, get personal points
      if (isAuthenticated) {
        const pointsRes = await axios.get(`${API}/loyalty/my-points/${id}`);
        setLoyaltyData(pointsRes.data);
      }
    } catch (error) {
      console.error('Error fetching loyalty data:', error);
    }
  }, [id, isAuthenticated]);

  // Fetch stamp card data
  const fetchStampCardData = useCallback(async () => {
    try {
      // Get public info
      const infoRes = await axios.get(`${API}/stampcard/restaurant/${id}/info`);
      setStampCardInfo(infoRes.data);
      
      // If authenticated, get personal card
      if (isAuthenticated) {
        const cardRes = await axios.get(`${API}/stampcard/my-card/${id}`);
        setStampCardData(cardRes.data);
      }
    } catch (error) {
      console.error('Error fetching stamp card data:', error);
    }
  }, [id, isAuthenticated]);

  // Redeem a reward
  const handleRedeemReward = async (rewardId) => {
    if (!isAuthenticated) {
      toast.error('Connectez-vous pour utiliser vos points');
      return;
    }
    
    setRedeemingReward(true);
    try {
      const response = await axios.post(`${API}/loyalty/redeem/${id}`, 
        { reward_id: rewardId },
        {}
      );
      toast.success(response.data.message);
      fetchLoyaltyData(); // Refresh data
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erreur lors de l'échange"));
    } finally {
      setRedeemingReward(false);
    }
  };

  const toggleFavoriteDish = async (dishId, e) => {
    e?.stopPropagation();
    if (!isAuthenticated) {
      toast.error('Connectez-vous pour ajouter aux favoris');
      return;
    }

    try {
      const isFavorite = favoriteDishes.includes(dishId);
      
      if (isFavorite) {
        await axios.delete(`${API}/favorites/dishes/${dishId}`);
        setFavoriteDishes(prev => prev.filter(id => id !== dishId));
        toast.success('Retiré des favoris');
      } else {
        await axios.post(`${API}/favorites/dishes/${dishId}`, {});
        setFavoriteDishes(prev => [...prev, dishId]);
        toast.success('Ajouté aux favoris');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const toggleFavoriteRestaurant = async () => {
    if (!isAuthenticated) {
      toast.error('Connectez-vous pour ajouter aux favoris');
      return;
    }

    try {
      
      if (favoriteRestaurant) {
        await axios.delete(`${API}/favorites/${id}`);
        setFavoriteRestaurant(false);
        toast.success('Restaurant retiré des favoris');
      } else {
        await axios.post(`${API}/favorites/${id}`, {});
        setFavoriteRestaurant(true);
        toast.success('Restaurant ajouté aux favoris');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  useEffect(() => {
    fetchRestaurantData();
    fetchReviews();
    checkCanReview();
    fetchFavoriteStatus();
    fetchLoyaltyData();
    fetchStampCardData();
  }, [fetchRestaurantData, fetchReviews, checkCanReview, fetchFavoriteStatus, fetchLoyaltyData, fetchStampCardData]);

  const handleOpenDishModal = (dish) => {
    try {
      setSelectedDish(dish);
      setDishQuantity(1);
      setSelectedOptions({});
      setSpecialInstructions('');
      
      // Initialize ingredients by category if customizable
      const categories = dish?.ingredients?.enabled && Array.isArray(dish?.ingredients?.categories)
        ? dish.ingredients.categories
        : [];
      if (categories.length > 0) {
        const initialSelection = {};
        categories.forEach(category => {
          if (category?.id) {
            initialSelection[category.id] = [];
          }
        });
        setSelectedIngredients(initialSelection);
      } else {
        setSelectedIngredients({});
      }
    } catch (err) {
      console.error('[handleOpenDishModal] Error:', err, 'Dish:', JSON.stringify(dish));
      toast.error('Erreur lors de l\'ouverture du plat');
      setSelectedDish(null);
    }
  };

  const handleCloseDishModal = () => {
    setSelectedDish(null);
    setDishQuantity(1);
    setSelectedOptions({});
    setSelectedIngredients({});
    setSpecialInstructions('');
  };

  const toggleIngredient = (categoryId, itemId, category) => {
    setSelectedIngredients(prev => {
      const currentSelection = prev[categoryId] || [];
      const isSelected = currentSelection.includes(itemId);
      const maxSel = Number(category?.max_selection) || 10;
      
      if (isSelected) {
        return { ...prev, [categoryId]: currentSelection.filter(id => id !== itemId) };
      } else {
        if (maxSel === 1) {
          return { ...prev, [categoryId]: [itemId] };
        }
        if (currentSelection.length >= maxSel) {
          toast.error(`Maximum ${maxSel} choix pour "${category?.name || 'cette catégorie'}"`);
          return prev;
        }
        return { ...prev, [categoryId]: [...currentSelection, itemId] };
      }
    });
  };

  const handleOptionChange = (optionId, choiceId, isMultiple = false) => {
    setSelectedOptions(prev => {
      if (isMultiple) {
        const current = prev[optionId] || [];
        if (current.includes(choiceId)) {
          return { ...prev, [optionId]: current.filter(v => v !== choiceId) };
        }
        return { ...prev, [optionId]: [...current, choiceId] };
      }
      return { ...prev, [optionId]: choiceId };
    });
  };

  const calculateDishPrice = () => {
    try {
      if (!selectedDish) return 0;
      let price = Number(selectedDish.price) || 0;
      
      // Add options price
      const options = Array.isArray(selectedDish.options) ? selectedDish.options : [];
      options.forEach(option => {
        const selected = selectedOptions[option?.id];
        if (selected) {
          const choices = Array.isArray(option?.choices) ? option.choices : [];
          choices.forEach(choice => {
            if (option?.type === 'multiple_choice') {
              if (Array.isArray(selected) && selected.includes(choice?.id)) {
                price += Number(choice?.price || choice?.price_delta) || 0;
              }
            } else {
              if (selected === choice?.id) {
                price += Number(choice?.price || choice?.price_delta) || 0;
              }
            }
          });
        }
      });
      
      // Add ingredients price based on categories
      const ingredientCategories = selectedDish.ingredients?.enabled && Array.isArray(selectedDish.ingredients?.categories)
        ? selectedDish.ingredients.categories
        : [];
      ingredientCategories.forEach(category => {
        const categorySelection = selectedIngredients[category?.id] || [];
        const freeQuantity = Number(category?.free_quantity) || 0;
        
        // Count selections and apply pricing
        let selectionCount = 0;
        const items = Array.isArray(category?.items) ? category.items : [];
        items.forEach(item => {
          if (categorySelection.includes(item?.id)) {
            selectionCount++;
            const itemPrice = Number(item?.price) || 0;
            if (itemPrice < 0) {
              price += itemPrice;
            } else if (selectionCount > freeQuantity && itemPrice > 0) {
              price += itemPrice;
            }
          }
        });
      });
      
      return price * dishQuantity;
    } catch (err) {
      console.error('[calculateDishPrice] Error:', err);
      return (Number(selectedDish?.price) || 0) * dishQuantity;
    }
  };

  const handleAddToCart = async () => {
    // Check if orders are accepted (can order even if physically closed)
    if (!canOrder && !selectedDish?.preorder_only) {
      toast.error(orderBlockMessage || 'Ce commerce n\'accepte pas de commandes en ce moment');
      return;
    }

    // Check required options
    for (const option of (Array.isArray(selectedDish?.options) ? selectedDish.options : [])) {
      if (option?.required && !selectedOptions[option?.id]) {
        toast.error(`Veuillez sélectionner: ${option?.name || 'une option'}`);
        return;
      }
    }

    // Check required ingredient categories
    const ingredientCats = selectedDish?.ingredients?.enabled && Array.isArray(selectedDish?.ingredients?.categories)
      ? selectedDish.ingredients.categories
      : [];
    for (const category of ingredientCats) {
      const selection = selectedIngredients[category?.id] || [];
      const minSel = Number(category?.min_selection) || 0;
      if (minSel > 0 && selection.length < minSel) {
        toast.error(`Veuillez sélectionner au moins ${minSel} ${category?.name || 'ingrédient(s)'}`);
        return;
      }
    }

    // Prepare ingredients data if customizable
    const ingredientsData = selectedDish.ingredients?.enabled 
      ? selectedIngredients 
      : null;

    // Dish data for guest cart
    const dishData = {
      ...selectedDish,
      restaurant_id: restaurant?.id,
      restaurant: restaurant ? { id: restaurant.id, name: restaurant.name, address: restaurant.address, logo: restaurant.logo } : null
    };

    try {
      await addToCart(
        selectedDish.id,
        dishQuantity,
        Object.keys(selectedOptions).length > 0 ? selectedOptions : null,
        specialInstructions || null,
        ingredientsData,
        dishData
      );
      toast.success(selectedDish.preorder_only ? 'Pré-commande ajoutée au panier!' : 'Article ajouté au panier!');
      handleCloseDishModal();
    } catch (error) {
      if (error?.isCartTypeMismatch) {
        // Show confirmation dialog to clear cart and switch type
        setCartMismatchDialog({ open: true, pendingDish: {
          id: selectedDish.id,
          quantity: dishQuantity,
          options: Object.keys(selectedOptions).length > 0 ? selectedOptions : null,
          instructions: specialInstructions || null,
          ingredients: ingredientsData,
          isPreorder: selectedDish.preorder_only,
          dishData
        }});
      } else {
        toast.error(typeof error === 'string' ? error : error?.message || 'Erreur lors de l\'ajout au panier');
      }
    }
  };

  const handleConfirmCartSwitch = async () => {
    const pending = cartMismatchDialog.pendingDish;
    if (!pending) return;
    
    try {
      await clearAndAddToCart(
        pending.id,
        pending.quantity,
        pending.options,
        pending.instructions,
        pending.ingredients,
        pending.dishData
      );
      toast.success(pending.isPreorder ? 'Panier remplacé - Pré-commande ajoutée!' : 'Panier remplacé - Article ajouté!');
      handleCloseDishModal();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Erreur lors du remplacement du panier');
    } finally {
      setCartMismatchDialog({ open: false, pendingDish: null });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse">
          <div className="h-64 bg-gray-200" />
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8" />
            <div className="grid md:grid-cols-2 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-200 rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!restaurant) return null;

  return (
    <Layout>
      {restaurant && (
        <PageSEO.Restaurant
          name={restaurant.name}
          description={restaurant.description}
          image={restaurant.cover_image ? `https://lemo-food.com/api${restaurant.cover_image.startsWith('/') ? '' : '/'}${restaurant.cover_image}` : null}
          id={restaurant.id}
          address={restaurant.address}
          rating={restaurant.avg_rating}
          reviewCount={restaurant.review_count}
        />
      )}
      {/* Cover Image */}
      <div className="relative h-48 sm:h-64 lg:h-80">
        <img 
          src={getFullImageUrl(restaurant.cover_image) || RESTAURANT_PLACEHOLDER}
          alt={restaurant.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        
        {/* Action Buttons (Share + Favorite) */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          {/* Share Button */}
          <ShareButtons 
            title={restaurant.name}
            text={`Découvrez ${restaurant.name} sur LEMO - Click & Collect près de chez vous !`}
          />
          
          {/* Favorite Restaurant Button */}
          <button
            onClick={toggleFavoriteRestaurant}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-lg ${
              favoriteRestaurant
                ? 'bg-red-500 text-white'
                : 'bg-white/90 text-gray-600 hover:text-red-500'
            }`}
            data-testid="favorite-restaurant-button"
          >
            <Heart className={`w-5 h-5 ${favoriteRestaurant ? 'fill-current' : ''}`} />
          </button>
        </div>
        
        {/* Restaurant Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto flex items-end gap-4">
            {restaurant.logo && (
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-white shadow-lg overflow-hidden shrink-0">
                <img src={getFullImageUrl(restaurant.logo)} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="text-white flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl sm:text-3xl font-bold">{restaurant.name}</h1>
                {/* Open/Closed Badge */}
                {openStatus.isOpen ? (
                  <Badge className="bg-emerald-500 text-white border-0 shadow-sm">
                    <span className="w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-pulse"></span>
                    Ouvert
                  </Badge>
                ) : (
                  <>
                    <Badge className="bg-red-500 text-white border-0 shadow-sm">
                      Fermé
                    </Badge>
                    {acceptingOrders && (
                      <Badge className="bg-emerald-600 text-white border-0 shadow-sm text-xs" data-testid="accepting-orders-badge">
                        Commandes acceptées
                      </Badge>
                    )}
                  </>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1 text-sm text-white/90">
                {restaurant.avg_rating > 0 && (
                  <button 
                    onClick={() => setShowReviews(true)}
                    className="flex items-center gap-1 hover:text-white transition-colors"
                  >
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    {restaurant.avg_rating?.toFixed(1) || '0.0'}
                    <span className="text-white/70">({restaurant.total_reviews || 0} avis)</span>
                  </button>
                )}
                <span>{restaurant.cuisine_type?.join(', ')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Restaurant Details */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6 mb-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Opening Hours */}
            <Collapsible open={showAllHours} onOpenChange={setShowAllHours}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${openStatus.isOpen ? 'bg-emerald-100' : 'bg-red-100'}`}>
                  <CalendarDays className={`w-5 h-5 ${openStatus.isOpen ? 'text-emerald-600' : 'text-red-600'}`} />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500">Horaires</p>
                  <CollapsibleTrigger className="flex items-center gap-1 group cursor-pointer">
                    <p className={`font-semibold ${openStatus.isOpen ? 'text-emerald-600' : 'text-red-600'}`}>
                      {openStatus.isOpen ? 'Ouvert' : 'Fermé'}
                      {openStatus.message && openStatus.message !== 'Fermé' && (
                        <span className="text-gray-500 font-normal ml-1">• {openStatus.message}</span>
                      )}
                    </p>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showAllHours ? 'rotate-180' : ''}`} />
                  </CollapsibleTrigger>
                </div>
              </div>
              <CollapsibleContent className="mt-3 ml-13 pl-[52px]">
                <div className="bg-gray-50 rounded-xl p-3 space-y-1.5">
                  {schedule.map(({ day, dayLabel, hours, isToday }) => (
                    <div 
                      key={day} 
                      className={`flex justify-between text-sm ${isToday ? 'font-semibold text-emerald-600' : 'text-gray-600'}`}
                    >
                      <span>{dayLabel}{isToday && ' (Aujourd\'hui)'}</span>
                      <span className={hours === 'Fermé' ? 'text-red-500' : ''}>{hours}</span>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Temps de préparation</p>
                <p className="font-semibold">{restaurant.avg_prep_time} min</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Adresse</p>
                <p className="font-semibold text-sm">{restaurant.address}, {restaurant.city}</p>
              </div>
            </div>
            {restaurant.phone && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Téléphone</p>
                  <p className="font-semibold">{restaurant.phone}</p>
                </div>
              </div>
            )}
          </div>
          {restaurant.description && (
            <p className="mt-4 text-gray-600 border-t border-gray-100 pt-4">{restaurant.description}</p>
          )}
          
          {/* Accepted Payment Methods at Counter */}
          {restaurant.accepted_payment_methods && restaurant.accepted_payment_methods.length > 0 && (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <p className="text-sm text-gray-500 mb-2">Paiements acceptes au comptoir</p>
              <div className="flex flex-wrap gap-2">
                {restaurant.accepted_payment_methods.map(method => {
                  const labels = {
                    cash: 'Especes',
                    card: 'CB',
                    ticket_restaurant_card: 'Carte resto',
                    ticket_restaurant_paper: 'Tickets resto',
                    american_express: 'Amex',
                    bitcoin: 'Bitcoin'
                  };
                  return (
                    <span 
                      key={method}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-sm text-gray-700"
                    >
                      {labels[method] || method}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Online Payment Status */}
          {restaurant.online_payment_enabled === false && (
            <div className="mt-4 border-t border-gray-100 pt-4" data-testid="online-payment-disabled-badge">
              <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                <CreditCard className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-700">
                  Le paiement en ligne n'est pas disponible. Reglement sur place uniquement.
                </p>
              </div>
            </div>
          )}

          {/* Gallery Photos */}
          {restaurant.gallery && restaurant.gallery.length > 0 && (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <p className="text-sm text-gray-500 mb-3">📷 Photos de l'établissement</p>
              <div className="grid grid-cols-3 gap-2">
                {restaurant.gallery.slice(0, 6).map((photo, index) => (
                  <button
                    key={index}
                    onClick={() => setGalleryModal({ open: true, index })}
                    className="aspect-square rounded-lg overflow-hidden hover:opacity-90 transition-opacity"
                  >
                    <img
                      src={getFullImageUrl(photo)}
                      alt={`Photo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Closed Restaurant Warning Banner - only show if also not accepting orders */}
        {!openStatus.isOpen && !acceptingOrders && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="font-semibold text-red-700">Restaurant actuellement fermé</p>
                <p className="text-sm text-red-600">
                  {orderBlockMessage || openStatus.message || 'Les commandes ne sont pas disponibles pour le moment'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Orders Paused Banner - restaurant open but orders paused */}
        {openStatus.isOpen && !acceptingOrders && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4" data-testid="orders-paused-banner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-semibold text-amber-700">Commandes temporairement indisponibles</p>
                <p className="text-sm text-amber-600">
                  {orderBlockMessage || 'Ce commerce n\'accepte pas de commandes en ce moment'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Restaurant Announcement Banner */}
        {restaurant.announcement_active && restaurant.announcement && (
          <div className={`rounded-xl p-4 mb-4 text-white shadow-lg ${
            restaurant.announcement_type === 'promo' 
              ? 'bg-gradient-to-r from-emerald-500 to-emerald-600'
              : restaurant.announcement_type === 'alert'
              ? 'bg-gradient-to-r from-red-500 to-red-600'
              : 'bg-gradient-to-r from-blue-500 to-blue-600'
          }`} data-testid="restaurant-announcement">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                <span className="text-xl">
                  {restaurant.announcement_type === 'promo' ? '🎉' 
                   : restaurant.announcement_type === 'alert' ? '⚠️' 
                   : '📢'}
                </span>
              </div>
              <div className="flex-1">
                <p className="font-semibold text-white/90 text-sm mb-1">
                  {restaurant.announcement_type === 'promo' ? 'Offre spéciale' 
                   : restaurant.announcement_type === 'alert' ? 'Information importante' 
                   : 'Actualité du restaurant'}
                </p>
                <p className="text-white">{restaurant.announcement}</p>
              </div>
            </div>
          </div>
        )}

        {/* Loyalty Program Banner */}
        {loyaltyProgram?.enabled && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Award className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Programme de fidélité</p>
                  {loyaltyData?.program_enabled ? (
                    <p className="text-sm text-amber-700">
                      <span className="font-bold">{loyaltyData.current_points}</span> points 
                      {loyaltyData.available_rewards?.some(r => r.can_redeem) && (
                        <span className="ml-2 text-emerald-600">• Récompense disponible !</span>
                      )}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-600">
                      Gagnez <span className="font-semibold">{loyaltyProgram.points_per_euro} point</span> par euro dépensé
                      {loyaltyProgram.welcome_bonus > 0 && (
                        <span className="ml-1">+ {loyaltyProgram.welcome_bonus} pts de bienvenue</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
              <Button 
                onClick={() => setShowLoyaltyModal(true)}
                variant="outline"
                className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-100"
                data-testid="loyalty-details-btn"
              >
                {loyaltyData?.current_points > 0 ? 'Mes points' : 'Voir les récompenses'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Stamp Card Banner */}
        {stampCardInfo?.enabled && (
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <Stamp className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{stampCardInfo.name || 'Carte à Tampons'}</p>
                  {stampCardData?.card_enabled ? (
                    <p className="text-sm text-orange-700">
                      <span className="font-bold">{stampCardData.current_stamps}/{stampCardData.stamps_required}</span> tampons
                      {stampCardData.active_rewards?.length > 0 && (
                        <span className="ml-2 text-emerald-600">• Récompense disponible !</span>
                      )}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-600">
                      {stampCardInfo.description || `${stampCardInfo.stamps_required} tampons = ${stampCardInfo.reward_product_name || '1 produit'} offert`}
                    </p>
                  )}
                </div>
              </div>
              <Button 
                onClick={() => setShowStampCardModal(true)}
                variant="outline"
                className="rounded-xl border-orange-300 text-orange-700 hover:bg-orange-100"
                data-testid="stampcard-details-btn"
              >
                {stampCardData?.current_stamps > 0 ? 'Ma carte' : 'Voir'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Menu Categories Sidebar */}
          <div className="lg:w-64 shrink-0">
            <div className="sticky top-24 bg-white rounded-2xl border border-gray-100 p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Catégories</h3>
              <nav className="space-y-1">
                {menu.map((section) => (
                  <button
                    key={section.category.id}
                    onClick={() => handleLockedCategoryClick(section)}
                    className={`w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                      activeCategory === section.category.id
                        ? 'bg-emerald-50 text-emerald-600'
                        : section.locked && !isCategoryAccessible(section)
                          ? 'text-amber-600 hover:bg-amber-50'
                          : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {section.locked && !isCategoryAccessible(section) && (
                      <Lock className="w-4 h-4 text-amber-500" />
                    )}
                    <span className="flex-1">{section.category.name}</span>
                    <span className="text-gray-400">
                      {section.locked && !isCategoryAccessible(section) ? '🔒' : `(${(section.dishes || []).length})`}
                    </span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Menu Items */}
          <div className="flex-1">
            {menu.map((section) => (
              <div 
                key={section.category.id} 
                id={`category-${section.category.id}`}
                className="mb-8"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-4 sticky top-20 bg-gray-50 py-2 z-10 flex items-center gap-2">
                  {section.category.name}
                  {section.locked && !isCategoryAccessible(section) && (
                    <Badge className="bg-amber-100 text-amber-700 ml-2">
                      <Lock className="w-3 h-3 mr-1" /> Verrouillée
                    </Badge>
                  )}
                </h2>
                
                {/* Locked Category Message */}
                {section.locked && !isCategoryAccessible(section) ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 text-center">
                    <Lock className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-amber-800 mb-2">Catégorie protégée</h3>
                    <p className="text-amber-600 mb-4">Cette catégorie est protégée par un code PIN.</p>
                    <Button 
                      onClick={() => setPinModal({ open: true, category: section.category })}
                      className="bg-amber-500 hover:bg-amber-600"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Entrer le code PIN
                    </Button>
                  </div>
                ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {(section.dishes || []).filter(dish => dish?.is_available !== false).map((dish) => {
                    const isPreorderOnly = dish.preorder_only === true;
                    const preorderDays = dish.preorder_delay_days || 0;
                    
                    return (
                      <div
                        key={dish.id}
                        className={`relative flex gap-4 p-4 bg-white rounded-2xl border transition-all text-left group ${
                          isPreorderOnly
                            ? 'border-blue-200 bg-blue-50/30'
                            : 'border-gray-100 hover:border-emerald-200 hover:shadow-md'
                        }`}
                        data-testid={`dish-${dish.id}`}
                      >
                        {/* Favorite Button */}
                        <button
                          onClick={(e) => toggleFavoriteDish(dish.id, e)}
                          className={`absolute top-2 right-2 z-20 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                            favoriteDishes.includes(dish.id)
                              ? 'bg-red-500 text-white'
                              : 'bg-white shadow-sm text-gray-400 hover:text-red-500'
                          }`}
                        >
                          <Heart className={`w-4 h-4 ${favoriteDishes.includes(dish.id) ? 'fill-current' : ''}`} />
                        </button>

                        {/* Preorder Badge */}
                        {isPreorderOnly && (
                          <div className="absolute top-2 left-2 z-20" data-testid={`preorder-badge-${dish.id}`}>
                            <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">
                              <Clock className="w-3 h-3 mr-1" />
                              Pre-commande ({preorderDays}j min.)
                            </Badge>
                          </div>
                        )}
                        
                        {/* Clickable area for dish modal */}
                        <button
                          onClick={() => handleOpenDishModal(dish)}
                          className="flex gap-4 flex-1 text-left cursor-pointer"
                        >
                        <div className="w-24 h-24 rounded-xl overflow-hidden shrink-0 bg-gray-100">
                          {dish.image ? (
                            <img 
                              src={getFullImageUrl(dish.image)}
                              alt={dish.name}
                              className="w-full h-full object-cover transition-transform group-hover:scale-105"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentNode.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-400 text-3xl">🍽️</div>';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400 text-3xl">
                              🍽️
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold transition-colors text-gray-900 group-hover:text-emerald-600">
                            {dish.name}
                          </h3>
                          {dish.description && (
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{dish.description}</p>
                          )}
                          {/* Badges for allergens and customization */}
                          {(dish.allergens?.length > 0 || dish.ingredients?.enabled || dish.online_only || section.category?.online_only) && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {(dish.online_only || section.category?.online_only) && (
                                <Badge
                                  variant="outline"
                                  className="text-xs gap-1 text-indigo-700 border-indigo-300 bg-indigo-50"
                                  data-testid={`online-only-badge-${dish.id}`}
                                >
                                  <CreditCard className="w-3 h-3" />
                                  Paiement en ligne uniquement
                                </Badge>
                              )}
                              {dish.allergens?.length > 0 && (
                                <Badge variant="outline" className="text-xs gap-1 text-orange-600 border-orange-200">
                                  <AlertTriangle className="w-3 h-3" />
                                  {dish.allergens.length} allergène{dish.allergens.length > 1 ? 's' : ''}
                                </Badge>
                              )}
                              {dish.ingredients?.enabled && (
                                <Badge variant="outline" className="text-xs gap-1 text-emerald-600 border-emerald-200">
                                  <Leaf className="w-3 h-3" />
                                  Personnalisable
                                </Badge>
                              )}
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-emerald-600">
                                {dish.ingredients?.enabled ? 'À partir de ' : ''}{(Number(dish.price) || 0).toFixed(2)} €
                              </span>
                              {/* Low stock warning */}
                              {dish.stock !== null && dish.stock !== undefined && dish.stock > 0 && dish.stock <= 5 && (
                                <span className="text-xs text-orange-600 font-medium">
                                  ({dish.stock} restant{dish.stock > 1 ? 's' : ''})
                                </span>
                              )}
                            </div>
                            {!isPreorderOnly && (
                              <span className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <Plus className="w-5 h-5" />
                              </span>
                            )}
                            {isPreorderOnly && (
                              <span className="text-xs text-blue-600 font-medium">
                                Sur commande
                              </span>
                            )}
                          </div>
                        </div>
                        </button>
                      </div>
                    );
                  })}
                </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating Cart Button */}
      {itemCount > 0 && (
        <div className="fixed bottom-20 md:bottom-6 left-4 right-4 md:left-auto md:right-6 z-40">
          <Button
            onClick={() => navigate('/checkout')}
            className="w-full md:w-auto bg-emerald-500 hover:bg-emerald-600 rounded-full px-6 py-6 shadow-lg"
            data-testid="view-cart-button"
          >
            <ShoppingBag className="w-5 h-5 mr-2" />
            Voir le panier ({itemCount})
            <span className="ml-2 font-bold">{cart.total_amount?.toFixed(2)} €</span>
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      )}

      {/* Dish Modal */}
      <Dialog open={!!selectedDish} onOpenChange={() => handleCloseDishModal()}>
        <DialogContent className="max-w-lg max-h-[85vh] p-0 flex flex-col overflow-hidden">
          <ErrorBoundary>
          {selectedDish && (
            <>
              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto">
                {/* Image - smaller, scrolls with content */}
                {selectedDish.image && (
                  <div className="relative w-full h-36 sm:h-44 overflow-hidden">
                    <img 
                      src={getFullImageUrl(selectedDish.image)} 
                      alt={selectedDish.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-transparent pointer-events-none" />
                  </div>
                )}
                
                {/* Content */}
                <div className="p-4 sm:p-5 space-y-3">
                  <DialogHeader>
                    <DialogTitle className="text-lg sm:text-xl">{selectedDish.name}</DialogTitle>
                  </DialogHeader>
                  
                  {selectedDish.description && (
                    <p className="text-gray-600 text-sm">{selectedDish.description}</p>
                  )}
                  
                  <p className="text-base font-bold text-emerald-600">À partir de {(Number(selectedDish.price) || 0).toFixed(2)} €</p>

                  {/* Allergens Warning */}
                  {selectedDish.allergens?.length > 0 && (
                    <div className="p-2.5 bg-orange-50 rounded-xl border border-orange-100">
                      <div className="flex items-center gap-2 text-orange-700 mb-1.5">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="font-medium text-sm">Allergènes</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {selectedDish.allergens.map((allergen) => (
                          <Badge 
                            key={allergen} 
                            variant="outline" 
                            className="text-xs bg-white border-orange-200 text-orange-700"
                          >
                            {allergen}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Customizable Ingredients - Category Based */}
                {selectedDish.ingredients?.enabled && Array.isArray(selectedDish.ingredients?.categories) && selectedDish.ingredients.categories.length > 0 && (
                  <div className="border-t border-gray-100 pt-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Leaf className="w-5 h-5 text-emerald-500" />
                      <h4 className="font-semibold text-gray-900">Personnalisez votre plat</h4>
                    </div>

                    {selectedDish.ingredients.categories.map((category) => {
                      if (!category?.id) return null;
                      const selection = selectedIngredients[category.id] || [];
                      const isRequired = (Number(category.min_selection) || 0) > 0;
                      
                      return (
                        <div key={category.id} className="mb-4 last:mb-0">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-700">
                                {category.name}
                                {isRequired && <span className="text-red-500 ml-1">*</span>}
                              </p>
                              {category.free_quantity > 0 && (
                                <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-600 border-emerald-200">
                                  {category.free_quantity} gratuit{category.free_quantity > 1 ? 's' : ''}
                                </Badge>
                              )}
                            </div>
                            <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                              {selection.length}/{Number(category.max_selection) || 0}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2">
                            {(Array.isArray(category.items) ? category.items : []).map((item) => {
                              if (!item?.id) return null;
                              const isSelected = selection.includes(item.id);
                              const isAvailable = item.is_available !== false;
                              // Calculate if this item would be paid (beyond free_quantity)
                              const freeQty = Number(category.free_quantity) || 0;
                              const selectionIndex = selection.indexOf(item.id);
                              const wouldBePaid = isSelected 
                                ? selectionIndex >= freeQty 
                                : selection.length >= freeQty;
                              const itemPrice = Number(item.price) || 0;
                              
                              return (
                                <button
                                  key={item.id}
                                  onClick={() => isAvailable && toggleIngredient(category.id, item.id, category)}
                                  disabled={!isAvailable}
                                  className={`flex items-center justify-between p-3 rounded-xl text-sm transition-all ${
                                    !isAvailable 
                                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                      : isSelected 
                                        ? 'bg-emerald-50 border-2 border-emerald-400 text-emerald-700'
                                        : 'bg-gray-50 border-2 border-transparent hover:border-gray-200'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                                      isSelected ? 'bg-emerald-500 text-white' : 'bg-gray-200'
                                    }`}>
                                      {isSelected && <Check className="w-3 h-3" />}
                                    </div>
                                    <span className={!isAvailable ? 'line-through' : ''}>
                                      {item.name}
                                    </span>
                                  </div>
                                  {isAvailable && itemPrice > 0 && wouldBePaid && (
                                    <span className="font-medium text-amber-600">
                                      +{itemPrice.toFixed(2)} €
                                    </span>
                                  )}
                                  {isAvailable && itemPrice < 0 && (
                                    <span className="font-medium text-emerald-600">
                                      {itemPrice.toFixed(2)} €
                                    </span>
                                  )}
                                  {!isAvailable && <span className="text-xs">(épuisé)</span>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Options */}
                {(Array.isArray(selectedDish.options) ? selectedDish.options : []).map((option) => {
                  if (!option?.id) return null;
                  return (
                  <div key={option.id} className="border-t border-gray-100 pt-4">
                    <h4 className="font-semibold text-gray-900 mb-3">
                      {option.name || 'Option'}
                      {option.required && <span className="text-red-500 ml-1">*</span>}
                      {option.option_type === 'grams' && <span className="text-xs text-gray-400 ml-2">(grammes)</span>}
                      {option.option_type === 'liters' && <span className="text-xs text-gray-400 ml-2">(litres)</span>}
                    </h4>
                    
                    {/* Text input type */}
                    {option.option_type === 'text_input' ? (
                      <div>
                        <Input
                          value={selectedOptions[option.id] || ''}
                          onChange={(e) => handleOptionChange(option.id, e.target.value)}
                          placeholder={option.placeholder || 'Entrez votre réponse...'}
                          maxLength={option.max_length || 100}
                          className="rounded-xl"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          {(selectedOptions[option.id] || '').length}/{option.max_length || 100} caractères
                        </p>
                      </div>
                    ) : option.type === 'single_choice' || (!option.multiple && option.option_type !== 'checkbox') ? (
                      /* Single choice (radio buttons) for grams/liters or single_choice */
                      <RadioGroup
                        value={selectedOptions[option.id] || ''}
                        onValueChange={(value) => handleOptionChange(option.id, value)}
                      >
                        {(Array.isArray(option.choices) ? option.choices : []).map((choice) => {
                          if (!choice?.id) return null;
                          const choicePrice = Number(choice.price || choice.price_delta) || 0;
                          return (
                          <div key={choice.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <RadioGroupItem value={choice.id} id={choice.id} />
                              <Label htmlFor={choice.id} className="cursor-pointer">{choice.name}</Label>
                            </div>
                            {choicePrice > 0 && (
                              <span className="text-sm text-gray-500">+{choicePrice.toFixed(2)} €</span>
                            )}
                          </div>
                          );
                        })}
                      </RadioGroup>
                    ) : (
                      /* Multiple choice (checkboxes) */
                      <div className="space-y-2">
                        {(Array.isArray(option.choices) ? option.choices : []).map((choice) => {
                          if (!choice?.id) return null;
                          const choicePrice = Number(choice.price || choice.price_delta) || 0;
                          return (
                          <div key={choice.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Checkbox
                                id={choice.id}
                                checked={(selectedOptions[option.id] || []).includes(choice.id)}
                                onCheckedChange={() => handleOptionChange(option.id, choice.id, true)}
                              />
                              <Label htmlFor={choice.id} className="cursor-pointer">{choice.name}</Label>
                            </div>
                            {choicePrice > 0 && (
                              <span className="text-sm text-gray-500">+{choicePrice.toFixed(2)} €</span>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  );
                })}

                {/* Special Instructions */}
                <div className="border-t border-gray-100 pt-4">
                  <Label className="text-sm text-gray-600">Instructions spéciales (optionnel)</Label>
                  <Textarea
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    placeholder="Allergies, préférences..."
                    className="mt-2"
                  />
                </div>
                </div>
              </div>

              {/* Fixed Footer - Quantity & Add to Cart */}
              <div className="shrink-0 border-t border-gray-100 p-4 bg-white">
                {/* Preorder info message */}
                {selectedDish?.preorder_only && (
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-2" data-testid="preorder-info-modal">
                    <Clock className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-700">
                        Disponible en pre-commande uniquement
                      </p>
                      <p className="text-xs text-blue-600 mt-0.5">
                        Delai minimum : {selectedDish.preorder_delay_days || 1} jour(s). Selectionnez une date de retrait lors du paiement.
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
                  <button
                    onClick={() => setDishQuantity(Math.max(1, dishQuantity - 1))}
                    className="w-9 h-9 rounded-full hover:bg-white flex items-center justify-center transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-7 text-center font-semibold text-sm">{dishQuantity}</span>
                  <button
                    onClick={() => setDishQuantity(dishQuantity + 1)}
                    className="w-9 h-9 rounded-full hover:bg-white flex items-center justify-center transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <Button
                  onClick={handleAddToCart}
                  className={`flex-1 rounded-full h-11 ${
                    selectedDish?.preorder_only 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-emerald-500 hover:bg-emerald-600'
                  }`}
                  data-testid="add-to-cart-button"
                >
                  {selectedDish?.preorder_only ? (
                    <>Pre-commander • {calculateDishPrice().toFixed(2)} EUR</>
                  ) : (
                    <>Ajouter • {calculateDishPrice().toFixed(2)} EUR</>
                  )}
                </Button>
                </div>
              </div>
            </>
          )}
          </ErrorBoundary>
        </DialogContent>
      </Dialog>

      {/* Reviews Dialog */}
      <Dialog open={showReviews} onOpenChange={setShowReviews}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              Avis clients ({reviewsTotal})
            </DialogTitle>
          </DialogHeader>
          
          {/* Rating Summary & Distribution */}
          {restaurant && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="text-center">
                <p className="text-4xl font-bold text-gray-900">{restaurant.avg_rating?.toFixed(1) || '0.0'}</p>
                <div className="flex gap-0.5 mt-1 justify-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-5 h-5 ${
                        star <= Math.round(restaurant.avg_rating || 0)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'fill-gray-200 text-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-1">{reviewsTotal} avis</p>
                {reviewsPhotosCount > 0 && (
                  <p className="text-xs text-gray-400">{reviewsPhotosCount} avec photos</p>
                )}
              </div>
              
              {/* Distribution */}
              <div className="space-y-1">
                {[5, 4, 3, 2, 1].map((rating) => {
                  const count = reviewsDistribution[rating] || 0;
                  const percentage = reviewsTotal > 0 ? (count / reviewsTotal) * 100 : 0;
                  return (
                    <button
                      key={rating}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleReviewFilterChange(reviewSortBy, reviewRatingFilter === rating ? null : rating);
                      }}
                      className={`flex items-center gap-2 w-full text-left p-1.5 rounded-md transition-colors hover:bg-gray-100 ${reviewRatingFilter === rating ? 'bg-yellow-100 ring-1 ring-yellow-300' : ''}`}
                    >
                      <span className="text-xs font-medium w-4">{rating}</span>
                      <Star className="w-3 h-3 fill-yellow-400 text-yellow-400 shrink-0" />
                      <div className="flex-1 h-2.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-yellow-400 transition-all" style={{ width: `${percentage}%` }} />
                      </div>
                      <span className="text-xs text-gray-500 w-8 text-right">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Write Review Button */}
          {isAuthenticated && canReview.can_review && !showReviewForm && (
            <Button 
              onClick={() => setShowReviewForm(true)}
              className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-xl"
            >
              <Star className="w-4 h-4 mr-2" />
              Laisser un avis
            </Button>
          )}

          {/* Review Form */}
          {showReviewForm && (
            <div className="bg-emerald-50 rounded-xl p-4 space-y-4">
              <h3 className="font-semibold text-gray-900">Votre avis</h3>
              
              {/* Rating Selection */}
              <div>
                <label className="text-sm text-gray-600 block mb-2">Note</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setNewReview(prev => ({ ...prev, rating: star }))}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star className={`w-8 h-8 ${star <= newReview.rating ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200'}`} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Comment */}
              <div>
                <label className="text-sm text-gray-600 block mb-2">Commentaire (optionnel)</label>
                <Textarea
                  value={newReview.comment}
                  onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder="Partagez votre expérience..."
                  rows={3}
                  className="rounded-xl"
                />
              </div>

              {/* Photo Upload */}
              <div>
                <label className="text-sm text-gray-600 block mb-2">Photos (optionnel, max 3)</label>
                
                {/* Photo Previews */}
                {newReview.photos.length > 0 && (
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {newReview.photos.map((photo, index) => (
                      <div key={index} className="relative group">
                        <img 
                          src={getFullImageUrl(photo)}
                          alt={`Photo ${index + 1}`}
                          className="w-20 h-20 object-cover rounded-lg border-2 border-white shadow-sm"
                        />
                        <button
                          onClick={() => removePhoto(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload Button */}
                {newReview.photos.length < 3 && (
                  <label className="flex items-center justify-center gap-2 border-2 border-dashed border-emerald-300 rounded-xl p-4 cursor-pointer hover:bg-emerald-100 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      className="hidden"
                      disabled={uploadingPhotos}
                    />
                    {uploadingPhotos ? (
                      <>
                        <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
                        <span className="text-sm text-emerald-600">Upload en cours...</span>
                      </>
                    ) : (
                      <>
                        <Camera className="w-5 h-5 text-emerald-500" />
                        <span className="text-sm text-emerald-600">Ajouter des photos</span>
                      </>
                    )}
                  </label>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowReviewForm(false);
                    setNewReview({ rating: 5, comment: '', photos: [] });
                  }}
                  className="flex-1 rounded-xl"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleSubmitReview}
                  disabled={submittingReview || uploadingPhotos}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 rounded-xl"
                >
                  {submittingReview ? 'Envoi...' : 'Publier'}
                </Button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            <select
              value={reviewSortBy}
              onChange={(e) => handleReviewFilterChange(e.target.value, reviewRatingFilter)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5"
            >
              <option value="recent">Plus récents</option>
              <option value="rating_high">Note haute → basse</option>
              <option value="rating_low">Note basse → haute</option>
              <option value="with_photos">Avec photos</option>
            </select>
            {reviewRatingFilter && (
              <button
                onClick={() => handleReviewFilterChange(reviewSortBy, null)}
                className="text-sm bg-yellow-100 text-yellow-700 rounded-lg px-3 py-1.5 flex items-center gap-1"
              >
                {reviewRatingFilter} étoiles ✕
              </button>
            )}
          </div>
          
          {/* Reviews List */}
          <div className="space-y-4 mt-4">
            {reviews.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                {reviewRatingFilter ? `Aucun avis avec ${reviewRatingFilter} étoiles` : 'Aucun avis pour le moment'}
              </p>
            ) : (
              reviews.map((review) => (
                <div key={review.id} className="border-b border-gray-100 pb-4 last:border-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                        <span className="text-emerald-700 font-semibold text-sm">
                          {(review.user_name || review.client_name)?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{review.user_name || review.client_name}</span>
                          {review.is_verified_purchase && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs py-0">
                              <Check className="w-3 h-3 mr-0.5" />
                              Vérifié
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(review.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-0.5 mb-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-4 h-4 ${
                          star <= review.rating
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'fill-gray-200 text-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  
                  {review.comment && (
                    <p className="text-gray-600 mb-2">{review.comment}</p>
                  )}

                  {/* Photos */}
                  {review.photos && review.photos.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {review.photos.map((photo, idx) => (
                        <img key={idx} src={getFullImageUrl(photo)} alt="" className="w-16 h-16 object-cover rounded-lg" />
                      ))}
                    </div>
                  )}
                  
                  {(review.restaurant_response || review.response) && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-700">Réponse du restaurant:</p>
                      <p className="text-sm text-gray-600 mt-1">{review.restaurant_response || review.response}</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* PIN Verification Modal */}
      <VerifyPinModal
        open={pinModal.open}
        onOpenChange={(open) => setPinModal({ open, category: open ? pinModal.category : null })}
        onVerify={handleVerifyPin}
        categoryName={pinModal.category?.name || ''}
      />

      {/* Gallery Modal */}
      {galleryModal.open && restaurant?.gallery && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
          onClick={() => setGalleryModal({ open: false, index: 0 })}
        >
          {/* Close button */}
          <button
            onClick={() => setGalleryModal({ open: false, index: 0 })}
            className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors z-10"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          {/* Navigation arrows */}
          {galleryModal.index > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setGalleryModal(prev => ({ ...prev, index: prev.index - 1 }));
              }}
              className="absolute left-4 p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
          )}
          {galleryModal.index < restaurant.gallery.length - 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setGalleryModal(prev => ({ ...prev, index: prev.index + 1 }));
              }}
              className="absolute right-4 p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          )}

          {/* Image */}
          <img
            src={getFullImageUrl(restaurant.gallery[galleryModal.index])}
            alt={`Photo ${galleryModal.index + 1}`}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/50 rounded-full text-white text-sm">
            {galleryModal.index + 1} / {restaurant.gallery.length}
          </div>
        </div>
      )}

      {/* Loyalty Modal */}
      <Dialog open={showLoyaltyModal} onOpenChange={setShowLoyaltyModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              Programme de fidélité
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Points Balance */}
            {loyaltyData?.program_enabled && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 text-center">
                <p className="text-sm text-gray-600 mb-1">Vos points</p>
                <p className="text-4xl font-bold text-amber-600">
                  {loyaltyData.current_points}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Total cumulé : {loyaltyData.lifetime_points} pts
                </p>
              </div>
            )}

            {/* Active Vouchers */}
            {loyaltyData?.active_vouchers?.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Gift className="w-4 h-4 text-emerald-500" />
                  Mes bons à utiliser
                </h4>
                <div className="space-y-2">
                  {loyaltyData.active_vouchers.map(voucher => (
                    <div key={voucher.id} className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                      <p className="font-medium text-emerald-700">{voucher.reward_name}</p>
                      <p className="text-xs text-emerald-600">
                        Expire le {new Date(voucher.expires_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* How it works */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Comment ça marche ?</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <p>✓ Gagnez <span className="font-semibold">{loyaltyProgram?.points_per_euro || 1} point</span> par euro dépensé</p>
                {loyaltyProgram?.welcome_bonus > 0 && (
                  <p>✓ <span className="font-semibold">{loyaltyProgram.welcome_bonus} points</span> offerts à votre 1ère commande</p>
                )}
                <p>✓ Échangez vos points contre des récompenses</p>
              </div>
            </div>

            {/* Available Rewards */}
            {(loyaltyProgram?.rewards?.length > 0 || loyaltyData?.available_rewards?.length > 0) && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Récompenses disponibles</h4>
                <div className="space-y-2">
                  {(loyaltyData?.available_rewards || loyaltyProgram?.rewards || []).map(reward => (
                    <div 
                      key={reward.id}
                      className={`rounded-xl p-4 border-2 transition-all ${
                        reward.can_redeem
                          ? 'border-amber-300 bg-amber-50'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{reward.name}</p>
                          <p className="text-xs text-gray-500">
                            {reward.type === 'discount' 
                              ? `${reward.discount_amount}€ de réduction`
                              : reward.product_name || 'Produit offert'
                            }
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-amber-600">
                            {reward.points_cost} pts
                          </p>
                          {reward.can_redeem ? (
                            <Button 
                              size="sm"
                              onClick={() => handleRedeemReward(reward.id)}
                              disabled={redeemingReward}
                              className="mt-1 bg-amber-500 hover:bg-amber-600 text-xs rounded-lg"
                            >
                              {redeemingReward ? 'Chargement...' : 'Échanger'}
                            </Button>
                          ) : loyaltyData?.current_points ? (
                            <p className="text-xs text-gray-400 mt-1">
                              Encore {reward.points_cost - loyaltyData.current_points} pts
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Not authenticated message */}
            {!isAuthenticated && (
              <div className="text-center text-gray-500 text-sm">
                <p>Connectez-vous pour commencer à cumuler des points !</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Stamp Card Modal */}
      <Dialog open={showStampCardModal} onOpenChange={setShowStampCardModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stamp className="w-5 h-5 text-orange-500" />
              {stampCardInfo?.name || 'Carte à Tampons'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Stamp Card Visual */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 border border-orange-200">
              {/* Progress */}
              {stampCardData?.card_enabled && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Progression</span>
                    <span className="font-semibold text-orange-600">
                      {stampCardData.current_stamps}/{stampCardData.stamps_required}
                    </span>
                  </div>
                  <div className="h-3 bg-white rounded-full overflow-hidden border border-orange-200">
                    <div 
                      className="h-full bg-gradient-to-r from-orange-400 to-amber-500 rounded-full transition-all"
                      style={{ width: `${stampCardData.progress_percent}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Stamps Grid */}
              <div className="flex flex-wrap gap-2 justify-center mb-4">
                {Array.from({ length: stampCardInfo?.stamps_required || 10 }).map((_, i) => {
                  const isFilled = stampCardData?.card_enabled && i < stampCardData.current_stamps;
                  return (
                    <div 
                      key={i}
                      className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${
                        isFilled 
                          ? 'bg-orange-500 border-orange-500 text-white shadow-md' 
                          : 'border-orange-300 bg-white'
                      }`}
                    >
                      {isFilled && <Check className="w-4 h-4" />}
                    </div>
                  );
                })}
              </div>

              {/* Stamps to go */}
              {stampCardData?.card_enabled && stampCardData.stamps_to_reward > 0 && (
                <p className="text-center text-sm text-orange-600">
                  Plus que <span className="font-bold">{stampCardData.stamps_to_reward}</span> tampon{stampCardData.stamps_to_reward > 1 ? 's' : ''} !
                </p>
              )}
            </div>

            {/* Active Rewards */}
            {stampCardData?.active_rewards?.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Gift className="w-4 h-4 text-emerald-500" />
                  Récompense à utiliser !
                </h4>
                <div className="space-y-2">
                  {stampCardData.active_rewards.map(reward => (
                    <div key={reward.id} className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                      <p className="font-medium text-emerald-700">{reward.reward_product_name}</p>
                      <p className="text-xs text-emerald-600">
                        Expire le {new Date(reward.expires_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* How it works */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Comment ça marche ?</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <p>✓ 1 tampon {stampCardInfo?.stamp_condition === 'min_amount' 
                  ? `par commande de ${stampCardInfo.min_order_amount}€ ou plus` 
                  : 'à chaque commande'}</p>
                <p>✓ {stampCardInfo?.stamps_required || 10} tampons = <span className="font-semibold">{stampCardInfo?.reward_product_name || '1 produit'}</span> offert</p>
                <p>✓ Carte remise à zéro après utilisation</p>
              </div>
            </div>

            {/* Stats */}
            {stampCardData?.card_enabled && (
              <div className="flex justify-between text-sm text-gray-500">
                <span>Total tampons gagnés: {stampCardData.total_stamps_earned}</span>
                <span>Récompenses obtenues: {stampCardData.rewards_claimed}</span>
              </div>
            )}

            {/* Not authenticated message */}
            {!isAuthenticated && (
              <div className="text-center text-gray-500 text-sm">
                <p>Connectez-vous pour commencer à collecter des tampons !</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cart Type Mismatch Confirmation Dialog */}
      <Dialog open={cartMismatchDialog.open} onOpenChange={(open) => !open && setCartMismatchDialog({ open: false, pendingDish: null })}>
        <DialogContent className="max-w-sm" data-testid="cart-mismatch-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Changer de type de panier
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-gray-600">
              {cartMismatchDialog.pendingDish?.isPreorder
                ? 'Votre panier contient des articles en commande immédiate. Ajouter un article en pré-commande videra votre panier actuel.'
                : 'Votre panier contient des articles en pré-commande. Ajouter un article en commande immédiate videra votre panier actuel.'
              }
            </p>
            <p className="text-sm font-medium text-gray-900">
              Voulez-vous vider le panier et continuer ?
            </p>
          </div>
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1 rounded-xl"
              onClick={() => setCartMismatchDialog({ open: false, pendingDish: null })}
              data-testid="cart-mismatch-cancel"
            >
              Annuler
            </Button>
            <Button
              className="flex-1 rounded-xl bg-amber-500 hover:bg-amber-600"
              onClick={handleConfirmCartSwitch}
              data-testid="cart-mismatch-confirm"
            >
              Vider et continuer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
